import './styles.css'

import { FC, Fragment, useCallback, useMemo, useState } from 'react'

import AreaFormModal from '@/pages/projectList/components/ProjectTable/AreaFormModal'
import useInspectionAreas from '@/pages/projectList/hooks/useInspectionAreas'
import useProjects from '@/pages/projectList/hooks/useProjects'
import { buildDashboardUrl, buildEditorUrl } from '@/pages/projectList/utils'
import {
  Box,
  HStack,
  Icon,
  IconButton,
  Menu,
  Portal,
  Separator,
  Span,
  Square,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import {
  BackIcon,
  ChevronLeftIcon,
  DownloadIcon,
  EditorPageIcon,
  ExcelIcon,
  ExternalLink,
  GridIcon,
  HelpCircleIcon,
  ListIcon,
  LogoLight,
  MenuIcon,
  PCDAlignmentIcon,
  PDFIcon,
  SettingIcon,
} from '@editor/assets/icons'
import {
  BUTTON_FONT_SIZE,
  EDITOR_NAVBAR_HEIGHT,
  EDITOR_NAVBAR_HEIGHT_TABLET,
  EDITOR_NAVBAR_PADDING,
  GENERAL_CONTACT_URL,
  HELP_CENTER_URL,
  USER_TYPES,
} from '@editor/config/constants'
import { InspectionArea as EditorInspectionArea } from '@editor/interfaces/inspection'
import { addInspectionArea, setAreaSwitched } from '@editor/store/page'
import { Tooltip } from '@infraloop/common'
import {
  AreaSwitcher,
  BlueprintCADExportButton,
  BlueprintImageExportButton,
  CADExportButton,
  DownsampledPCDExportButton,
  InspectionAreaSwitcher,
  MeshZipExportButton,
  PCDExportButton,
  decideActionPermission,
  useAppDispatch,
  useAppSelector,
} from '@infraloop/editor'
import { useTranslation } from '@infraloop/i18n'
import mixpanel from 'mixpanel-browser'
import { LuBoxes, LuPencil } from 'react-icons/lu'
import { Link as RouterLink, useLocation, useMatches, useNavigate, useParams } from 'react-router'
import { useDebouncedCallback } from 'use-debounce'

import NavbarPageButton from './NavbarPageButton'

/**
 * The Hatsuly editor header. It composes the same building blocks `@infraloop/editor`
 * exports as `app/editor`'s header and behaves identically, differing in exactly two ways:
 * its back button returns to the in-app Hatsuly dashboard rather than Markly, and it adds
 * Edit-Area / Create-Area actions that open Hatsuly's area form.
 */
const Header: FC = () => {
  const { t } = useTranslation()

  // Store (editor)
  const dispatch = useAppDispatch()
  const userType = useAppSelector((state) => state.user.userType)
  const isPageLoading = useAppSelector((state) => state.page.isLoading)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const inspectionAreas = useAppSelector((state) => state.page.inspectionAreas)

  // Route matching to check current page
  const matches = useMatches()
  const isBlueprintPage = matches.some((row) => row.id === 'project-blueprint')
  const isBlueprintEditorPage = matches.some((row) => row.id === 'project-blueprint-editor')
  const isEditorPage = matches.some((row) => row.id === 'project-editor')
  const isInspectionSheetPage = matches.some((row) => row.id.includes('inspection-sheet'))
  const isPCDAlignmentPage = matches.some((row) => row.id.includes('pcd-alignment'))

  // Get project ID from URL
  const { project_id } = useParams<{ project_id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const queries = new URLSearchParams(location.search)
  const inspection_area_id = queries.get('area')

  // Handles sub-menu
  const { onClose, onOpen, open } = useDisclosure()
  const debounceSubMenuClose = useDebouncedCallback(onClose, 200)
  const handleMouseEnter = useCallback(() => {
    debounceSubMenuClose.cancel()
    onOpen()
  }, [debounceSubMenuClose, onOpen])

  // Permissions
  const isOwner = useAppSelector((state) => state.page.isOwner)
  const isInvited = useAppSelector((state) => state.page.isInvited)
  const isAllowedToModify = decideActionPermission(isOwner, isInvited).PROJECT_DASHBOARD.MODIFY.includes(userType)

  // Back to the Hatsuly dashboard. The dashboard is served by this same app, so this is an
  // in-app (react-router) route — same as opening the editor from the dashboard. The project
  // and current-area ids ride in the hash so the dashboard selects the project's tab, expands
  // it, and scrolls to / highlights the area the user came from (ported from the legacy dashboard).
  const backToDashboard = buildDashboardUrl(project_id ?? '', inspection_area_id ?? undefined)

  // Hatsuly area data — feeds the Edit/Create Area form and stays on Hatsuly's data layer.
  const { ownedProjects, invitedProjects } = useProjects()
  const { areas, createArea, updateArea } = useInspectionAreas(project_id ?? '', Boolean(project_id))
  const project = useMemo(
    () => [...ownedProjects, ...invitedProjects].find((p) => p.project_id === project_id),
    [ownedProjects, invitedProjects, project_id],
  )
  const editingArea = useMemo(
    () => areas.find((a) => a.inspection_area_id === inspection_area_id),
    [areas, inspection_area_id],
  )

  // Area form modal state
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)

  /**
   * After creating an area, move into it on the 3D editor page. The area was created through
   * Hatsuly's data layer, so the editor's own store doesn't know about it — add it to the
   * store and switch to it directly (rather than waiting on a refetch, which would leave the
   * user on the previous area until it lands), then navigate to the editor route. Mirrors the
   * legacy dashboard's create-then-switch. After editing, refresh the current route so the
   * change is reflected (reusing the dashboard form's trade-off).
   */
  const handleCreateArea = useCallback(
    async (params: Parameters<typeof createArea>[0]) => {
      const created = await createArea(params)
      // The editor store and Hatsuly's data layer use parallel InspectionArea types for the
      // same backend shape; the created area is structurally what the editor store expects.
      dispatch(addInspectionArea(created as unknown as EditorInspectionArea))
      dispatch(setAreaSwitched(created.inspection_area_id))
      void navigate(buildEditorUrl(project_id!, created.inspection_area_id), { state: { navigated: true } })
      return created
    },
    [createArea, navigate, project_id, dispatch],
  )
  const handleUpdateArea = useCallback(
    async (params: Parameters<typeof updateArea>[0]) => {
      const updated = await updateArea(params)
      void navigate(0)
      return updated
    },
    [updateArea, navigate],
  )

  const editorItems = useMemo(
    () =>
      isEditorPage ? (
        <Menu.ItemGroup key="hamburger-menu-items-editor">
          <Menu.ItemGroupLabel ml={2}>エクスポート</Menu.ItemGroupLabel>
          <CADExportButton />
          {userType === USER_TYPES.ADMIN && <PCDExportButton />}
          {userType === USER_TYPES.ADMIN && <DownsampledPCDExportButton />}
          {userType === USER_TYPES.ADMIN && <MeshZipExportButton />}
        </Menu.ItemGroup>
      ) : null,
    [isEditorPage, userType],
  )

  const areaItems = useMemo(() => {
    if (!isAllowedToModify) return []

    const items = []

    // Create a new area — available whenever the user may modify the project.
    if (project) {
      items.push(
        <Menu.Item key="create-area" value="create-area" onClick={() => setIsCreateOpen(true)}>
          <Square className="icon-wrapper">
            <Icon as={LuBoxes} boxSize="1.2rem" />
          </Square>
          <Span>{t('hatsuly.area.create')}</Span>
        </Menu.Item>,
      )
    }

    // Edit the currently open area.
    if (project && editingArea) {
      items.push(
        <Menu.Item key="edit-area" value="edit-area" onClick={() => setIsEditOpen(true)}>
          <Square className="icon-wrapper">
            <Icon as={LuPencil} boxSize="1.2rem" />
          </Square>
          <Span>{t('hatsuly.menu.areaEdit')}</Span>
        </Menu.Item>,
      )
    }

    // Screen settings — editor page only (identical to app/editor's header).
    if (isEditorPage && inspectionArea) {
      items.push(
        <Menu.Item
          key="edit-settings"
          value="edit-settings"
          onClick={() => {
            window.dispatchEvent(new Event('editor:edit-settings'))
          }}
        >
          <Square className="icon-wrapper">
            <SettingIcon size="1.2rem" />
          </Square>
          <Span>画面設定</Span>
        </Menu.Item>,
      )
    }

    return items
  }, [inspectionArea, isEditorPage, isAllowedToModify, project, editingArea, t])

  const inspectionSheetItems = useMemo(
    () =>
      isInspectionSheetPage ? (
        <Fragment key="hamburger-menu-items-inspection-sheet">
          <Menu.ItemGroup>
            <Menu.ItemGroupLabel ml={2}>帳票詳細</Menu.ItemGroupLabel>
            <Menu.Root open={open} positioning={{ placement: 'right', offset: { mainAxis: 0, crossAxis: 0 } }}>
              <Menu.TriggerItem
                onMouseEnter={handleMouseEnter}
                onMouseLeave={debounceSubMenuClose}
                className="has-submenu"
              >
                <HStack>
                  <ChevronLeftIcon size="1.2rem" />
                  <Square className="icon-wrapper">
                    <Icon as={DownloadIcon} boxSize="1.2rem" />
                  </Square>
                  <Text>ダウンロード</Text>
                </HStack>
              </Menu.TriggerItem>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content
                    background="gray2.600"
                    border="1px solid {colors.gray2.500}"
                    width="210px"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={debounceSubMenuClose}
                  >
                    <Menu.Item
                      value="print-pdf"
                      color="white"
                      cursor="pointer"
                      onClick={() => {
                        mixpanel.track('Export inspection sheet', { 'File format': 'PDF' })
                        window.print()
                      }}
                    >
                      <Square className="icon-wrapper">
                        <PDFIcon size="1.2rem" />
                      </Square>
                      印刷 (PDF)
                    </Menu.Item>
                    <Menu.Item
                      value="export-xlsx"
                      color="white"
                      cursor="pointer"
                      onClick={() => {
                        window.dispatchEvent(new Event('export-xlsx'))
                      }}
                    >
                      <Square className="icon-wrapper">
                        <ExcelIcon size="1.2rem" />
                      </Square>
                      エクセル (XLSX)
                    </Menu.Item>
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          </Menu.ItemGroup>
          {isAllowedToModify && (
            <Menu.Item
              value="edit-project"
              onClick={() => {
                window.dispatchEvent(new Event('edit-project'))
              }}
            >
              <Square className="icon-wrapper">
                <SettingIcon size="1.2rem" />
              </Square>
              <Span>設定</Span>
            </Menu.Item>
          )}
        </Fragment>
      ) : null,
    [isInspectionSheetPage, debounceSubMenuClose, open, isAllowedToModify, handleMouseEnter],
  )

  const blueprintEditorItems = useMemo(
    () =>
      isBlueprintEditorPage ? (
        <Menu.ItemGroup key="hamburger-menu-items-blueprint-editor">
          <Menu.ItemGroupLabel ml={2}>エクスポート</Menu.ItemGroupLabel>
          <BlueprintCADExportButton />
          <BlueprintImageExportButton />
        </Menu.ItemGroup>
      ) : null,
    [isBlueprintEditorPage],
  )

  const menuItems = [editorItems, inspectionSheetItems, blueprintEditorItems].filter(Boolean)

  const areaSwitcherItems = useMemo(
    () =>
      inspectionAreas.map<InspectionAreaSwitcher>((area) => {
        const alignedAreaIds = Object.keys(area.pcd_alignments || {})
        const alignedAreaNames = alignedAreaIds
          .map((id) => inspectionAreas.find((ar) => ar.inspection_area_id === id))
          .filter(Boolean)
          .map((ar) => ar?.inspection_area_name)
          .join(', ')

        return {
          ...area,
          namePostfix: alignedAreaIds.length ? (
            <Tooltip label={alignedAreaNames} positioning={{ placement: 'top' }} showArrow>
              <Box ml={2}>
                <PCDAlignmentIcon size="1rem" />
              </Box>
            </Tooltip>
          ) : undefined,
        }
      }),
    [inspectionAreas],
  )

  if (!project_id) {
    return null
  }

  return (
    <HStack bg="gray2.900" p={EDITOR_NAVBAR_PADDING} w="100%" m={0} id="project-navbar" zIndex={150}>
      <HStack
        w="100%"
        h={{ base: EDITOR_NAVBAR_HEIGHT_TABLET, xl: EDITOR_NAVBAR_HEIGHT }}
        justifyContent="start"
        gap={4}
      >
        <Box
          asChild
          display="inline-flex"
          alignItems="center"
          fontSize={{ base: 'lg', xl: 'xs' }}
          h="100%"
          color="gray.300"
        >
          <RouterLink to={backToDashboard}>
            <BackIcon />
            {t('dashboard._single')}
          </RouterLink>
        </Box>
        <HStack
          gap={0}
          bg="gray2.800"
          overflow="hidden"
          borderRadius="md"
          separator={<Separator borderColor="whiteAlpha.200" />}
        >
          <>
            <NavbarPageButton
              id="editor-page-button"
              title="3D画面"
              tooltip={
                <Box>
                  <Text>3D画面に移動</Text>
                  {!inspection_area_id && <Text color="orange">エリアを選択してください</Text>}
                </Box>
              }
              icon={<EditorPageIcon />}
              path={`/projects/${project_id}/editor?area=${inspection_area_id}`}
              isActive={isEditorPage}
              isDisabled={!inspection_area_id || isPageLoading}
            />
            <NavbarPageButton
              id="pcd-alignment-button"
              title="位置合わせ"
              tooltip={
                <Box>
                  <Text>エリア位置合わせに移動</Text>
                  {!inspection_area_id && <Text color="orange">エリアを選択してください</Text>}
                </Box>
              }
              icon={<PCDAlignmentIcon />}
              path={
                !inspection_area_id
                  ? `/projects/${project_id}/pcd-alignment`
                  : `/projects/${project_id}/pcd-alignment?area=${inspection_area_id}`
              }
              isActive={isPCDAlignmentPage}
              isDisabled={!inspection_area_id || isPageLoading}
            />
            <NavbarPageButton
              id="blueprint-editor-page-button"
              title="設計図面"
              tooltip="設計図面に移動"
              icon={<GridIcon />}
              path={
                !inspection_area_id
                  ? `/projects/${project_id}/blueprint-editor`
                  : `/projects/${project_id}/blueprint-editor?area=${inspection_area_id}`
              }
              isActive={isBlueprintEditorPage}
              isDisabled={isPageLoading}
            />
            <NavbarPageButton
              id="inspection-sheet-page-button"
              title="帳票詳細"
              tooltip="帳票詳細に移動"
              icon={<ListIcon />}
              path={
                !inspection_area_id
                  ? `/projects/${project_id}/inspection-sheet`
                  : `/projects/${project_id}/inspection-sheet?area=${inspection_area_id}`
              }
              isActive={isInspectionSheetPage}
              isDisabled={isPageLoading}
            />
          </>
        </HStack>
      </HStack>

      <HStack
        display={{
          [isBlueprintPage ? 'xl' : 'md']: 'none',
        }}
      >
        <LogoLight height="28px" width="163px" />
      </HStack>

      <HStack w="100%" justifyContent="end" gap={2}>
        <AreaSwitcher inspectionAreas={areaSwitcherItems} />

        {/*
          Chakra's Menu unmounts its content on close. Some menu entries (the CAD export
          actions) own a dialog and long-running export state, so they must survive the menu
          closing - keep the content mounted once opened.
        */}
        <Menu.Root unmountOnExit={false}>
          <Menu.Trigger asChild>
            <IconButton
              data-testid="navbar-hamburger"
              bg="gray2.800"
              color="gray2.300"
              borderColor="gray2.500"
              borderWidth={1}
              _hover={{ bg: 'gray2.600' }}
              _active={{ bg: 'gray2.600' }}
            >
              <Square fontSize={BUTTON_FONT_SIZE}>
                <MenuIcon />
              </Square>
            </IconButton>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content
                className="navbar-hamburger"
                bg="gray2.600"
                border="1px solid {colors.gray2.500}"
                py={2}
                px={0}
              >
                {menuItems}

                {menuItems.length > 0 && <Menu.Separator bg="whiteAlpha.200" borderColor="whiteAlpha.200" />}

                {areaItems.length > 0 && (
                  <>
                    <Menu.ItemGroup>
                      <Menu.ItemGroupLabel ml={2}>エリア</Menu.ItemGroupLabel>
                      {areaItems.map((item) => item)}
                    </Menu.ItemGroup>
                    <Menu.Separator bg="whiteAlpha.200" borderColor="whiteAlpha.200" />
                  </>
                )}

                <Menu.ItemGroup>
                  <Menu.ItemGroupLabel ml={2}>ヘルプ</Menu.ItemGroupLabel>
                  <Menu.Item value="user-manual" asChild>
                    <RouterLink to={HELP_CENTER_URL} target="_blank">
                      <Square className="icon-wrapper">
                        <HelpCircleIcon size="1.2rem" />
                      </Square>
                      ユーザーマニュアル
                    </RouterLink>
                  </Menu.Item>

                  <Menu.Item value="contact" asChild>
                    <RouterLink to={GENERAL_CONTACT_URL} target="_blank">
                      <Square className="icon-wrapper">
                        <ExternalLink size="0.9rem" />
                      </Square>
                      お問い合わせ
                    </RouterLink>
                  </Menu.Item>
                </Menu.ItemGroup>

                <Menu.Separator bg="whiteAlpha.200" borderColor="whiteAlpha.200" />

                <Menu.ItemGroup>
                  <Menu.Item value="return-dashboard" asChild>
                    <RouterLink to={backToDashboard}>
                      <Square className="icon-wrapper">
                        <ListIcon size="1.4rem" />
                      </Square>
                      {t('dashboard.returnToDashboard')}
                    </RouterLink>
                  </Menu.Item>
                </Menu.ItemGroup>
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      </HStack>

      {project && (
        <>
          <AreaFormModal
            project={project}
            areas={areas}
            isOpen={isCreateOpen}
            onClose={() => setIsCreateOpen(false)}
            onCreate={handleCreateArea}
            onUpdate={handleUpdateArea}
          />
          {editingArea && isEditOpen && (
            <AreaFormModal
              project={project}
              areas={areas}
              area={editingArea}
              isOpen
              onClose={() => setIsEditOpen(false)}
              onCreate={handleCreateArea}
              onUpdate={handleUpdateArea}
            />
          )}
        </>
      )}
    </HStack>
  )
}

export default Header
